import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Switch,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Stack,
  Paper,
  Chip,
  Divider,
  Tooltip,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import ApiConfig from "../../../Config";
import collectionService from "../../../repository/collectionRepository";

const CollectionManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [creating, setCreating] = useState(false);

  const [togglingId, setTogglingId] = useState(null);

  const [desktopPreview, setDesktopPreview] = useState("");
  const [mobilePreview, setMobilePreview] = useState("");

  const [newCollection, setNewCollection] = useState({
    collectionTitle: "",
    collectionBanner: null,
    collectionBanner_mob: null,
  });

  const [editDialog, setEditDialog] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);

  const [editDesktopPreview, setEditDesktopPreview] = useState("");
  const [editMobilePreview, setEditMobilePreview] = useState("");

  const [editCollectionData, setEditCollectionData] = useState({
    collectionTitle: "",
    collectionBanner: null,
    collectionBanner_mob: null,
  });

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);

  const [statusDialog, setStatusDialog] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [statusCollection, setStatusCollection] = useState(null);

  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    cardAlt: isDark ? "#151412" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.10)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",
    success: isDark ? "#8FC6A3" : "#3D8054",
    danger: isDark ? "#D99494" : "#B54545",
  };

  const cardSx = {
    borderRadius: "14px",
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: isDark
      ? "0 8px 30px rgba(0,0,0,.14)"
      : "0 8px 30px rgba(50,40,30,.045)",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "9px",
      backgroundColor: colors.cardAlt,
      color: colors.text,
      "& fieldset": {
        borderColor: colors.border,
      },
      "&:hover fieldset": {
        borderColor: colors.accent,
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.accent,
      },
    },
    "& .MuiInputLabel-root": {
      color: colors.secondary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: colors.accent,
    },
    "& .MuiInputBase-input::placeholder": {
      color: colors.secondary,
    },
  };

  const fetchCollections = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      const res = await collectionService.getCollections();

      const data = Array.isArray(res.data?.collectionData)
        ? res.data.collectionData
        : [];

      setCollections(data);
    } catch (err) {
      console.error("Collection fetch error:", err);

      setError(err?.response?.data?.message || "Failed to fetch collections");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      setSuccess("");
    }, 3000);

    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      setError("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [error]);

  const handleToggleActive = (collectionId, isActive) => {
    const collection = collections.find(
      (item) => item.collectionId === collectionId,
    );

    if (!collection) return;

    setStatusCollection(collection);
    setStatusDialog(true);
  };

  const closeStatusDialog = () => {
    if (statusChanging) return;

    setStatusDialog(false);
    setStatusCollection(null);
  };

  const confirmToggleActive = async () => {
    if (!statusCollection) return;

    const collectionId = statusCollection.collectionId;

    const currentStatus = Boolean(statusCollection.isActive);

    try {
      setStatusChanging(true);
      setTogglingId(collectionId);
      setError("");

      await collectionService.setActiveCollection(collectionId, currentStatus);

      setCollections((prev) =>
        prev.map((collection) =>
          collection.collectionId === collectionId
            ? {
                ...collection,
                isActive: !currentStatus,
              }
            : collection,
        ),
      );

      setSuccess(
        !currentStatus
          ? "Collection activated successfully"
          : "Collection deactivated successfully",
      );

      setStatusDialog(false);
      setStatusCollection(null);
    } catch (err) {
      console.error("Collection status update failed:", err);

      setError(
        err?.response?.data?.message || "Failed to update collection status",
      );
    } finally {
      setStatusChanging(false);
      setTogglingId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (files?.length) {
      const file = files[0];

      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file");
        e.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError("Image size should be less than 5 MB");
        e.target.value = "";
        return;
      }

      if (name === "collectionBanner") {
        if (desktopPreview) {
          URL.revokeObjectURL(desktopPreview);
        }

        setDesktopPreview(URL.createObjectURL(file));
      }

      if (name === "collectionBanner_mob") {
        if (mobilePreview) {
          URL.revokeObjectURL(mobilePreview);
        }

        setMobilePreview(URL.createObjectURL(file));
      }

      setNewCollection((prev) => ({
        ...prev,
        [name]: file,
      }));

      return;
    }

    setNewCollection((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = useCallback(() => {
    if (desktopPreview) {
      URL.revokeObjectURL(desktopPreview);
    }

    if (mobilePreview) {
      URL.revokeObjectURL(mobilePreview);
    }

    setDesktopPreview("");
    setMobilePreview("");

    setNewCollection({
      collectionTitle: "",
      collectionBanner: null,
      collectionBanner_mob: null,
    });

    const desktopInput = document.getElementById("collection-desktop-upload");

    const mobileInput = document.getElementById("collection-mobile-upload");

    if (desktopInput) {
      desktopInput.value = "";
    }

    if (mobileInput) {
      mobileInput.value = "";
    }
  }, [desktopPreview, mobilePreview]);

  const handleCloseDialog = () => {
    if (creating) return;

    setOpenDialog(false);
    resetForm();
  };

  const handleAddCollection = async () => {
    if (!newCollection.collectionTitle.trim()) {
      setError("Collection title is required");
      return;
    }

    if (!newCollection.collectionBanner) {
      setError("Please select a desktop banner");
      return;
    }

    if (!newCollection.collectionBanner_mob) {
      setError("Please select a mobile banner");
      return;
    }

    try {
      setCreating(true);
      setError("");

      const formData = new FormData();

      formData.append("collectionTitle", newCollection.collectionTitle.trim());

      formData.append("collectionBanner", newCollection.collectionBanner);

      formData.append(
        "collectionBanner_mob",
        newCollection.collectionBanner_mob,
      );

      // await axios.post(
      //   `${ApiConfig.apiBaseUrl}${ApiConfig.createCollection}`,
      //   formData,
      //   {
      //     headers: {
      //       "Content-Type": "multipart/form-data",
      //     },
      //   },
      // );
      await collectionService.createCollection(formData);

      setOpenDialog(false);
      resetForm();

      await fetchCollections(true);

      setSuccess("Collection created successfully");
    } catch (err) {
      console.error("Collection creation failed:", err);

      setError(err?.response?.data?.message || "Failed to create collection");
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (collection) => {
    setEditingCollection(collection);

    setEditCollectionData({
      collectionTitle: collection.collectionTitle || "",
      collectionBanner: null,
      collectionBanner_mob: null,
    });

    setEditDesktopPreview(collection.collectionBanner || "");

    setEditMobilePreview(collection.collectionBanner_mob || "");

    setEditDialog(true);
  };

  const closeEditDialog = () => {
    if (editing) return;

    setEditDialog(false);
    setEditingCollection(null);

    if (editDesktopPreview && editDesktopPreview.startsWith("blob:")) {
      URL.revokeObjectURL(editDesktopPreview);
    }

    if (editMobilePreview && editMobilePreview.startsWith("blob:")) {
      URL.revokeObjectURL(editMobilePreview);
    }

    setEditDesktopPreview("");
    setEditMobilePreview("");

    setEditCollectionData({
      collectionTitle: "",
      collectionBanner: null,
      collectionBanner_mob: null,
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value, files } = e.target;

    if (files?.length) {
      const file = files[0];

      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file");
        e.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError("Image size should be less than 5 MB");
        e.target.value = "";
        return;
      }

      if (name === "collectionBanner") {
        if (editDesktopPreview && editDesktopPreview.startsWith("blob:")) {
          URL.revokeObjectURL(editDesktopPreview);
        }

        setEditDesktopPreview(URL.createObjectURL(file));
      }

      if (name === "collectionBanner_mob") {
        if (editMobilePreview && editMobilePreview.startsWith("blob:")) {
          URL.revokeObjectURL(editMobilePreview);
        }

        setEditMobilePreview(URL.createObjectURL(file));
      }

      setEditCollectionData((prev) => ({
        ...prev,
        [name]: file,
      }));

      return;
    }

    setEditCollectionData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditCollection = async () => {
    if (!editCollectionData.collectionTitle.trim()) {
      setError("Collection title is required");
      return;
    }

    if (!editingCollection?.collectionId) {
      setError("Collection ID is missing");
      return;
    }

    try {
      setEditing(true);
      setError("");

      const formData = new FormData();

      formData.append("collectionId", editingCollection.collectionId);

      formData.append(
        "collectionTitle",
        editCollectionData.collectionTitle.trim(),
      );

      if (editCollectionData.collectionBanner) {
        formData.append(
          "collectionBanner",
          editCollectionData.collectionBanner,
        );
      }

      if (editCollectionData.collectionBanner_mob) {
        formData.append(
          "collectionBanner_mob",
          editCollectionData.collectionBanner_mob,
        );
      }

      await collectionService.editCollection(formData);
      closeEditDialog();

      await fetchCollections(true);

      setSuccess("Collection updated successfully");
    } catch (err) {
      console.error("Collection update failed:", err);

      setError(err?.response?.data?.message || "Failed to update collection");
    } finally {
      setEditing(false);
    }
  };

  const openDeleteDialog = (collection) => {
    setSelectedCollection(collection);
    setDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;

    setDeleteDialog(false);
    setSelectedCollection(null);
  };

  const handleDeleteCollection = async () => {
    if (!selectedCollection?.collectionId) {
      setError("Collection ID is missing");
      return;
    }

    try {
      setDeleting(true);
      setError("");

      // await axios.post(`${ApiConfig.apiBaseUrl}${ApiConfig.deleteCollection}`, {
      //   collectionId: selectedCollection.collectionId,
      // });

      await collectionService.deleteCollection(selectedCollection.collectionId);
      setDeleteDialog(false);
      setSelectedCollection(null);

      setCollections((prev) =>
        prev.filter(
          (collection) =>
            collection.collectionId !== selectedCollection.collectionId,
        ),
      );

      setSuccess("Collection deleted successfully");
    } catch (err) {
      console.error("Collection deletion failed:", err);

      setError(err?.response?.data?.message || "Failed to delete collection");
    } finally {
      setDeleting(false);
    }
  };

  const CollectionCard = ({ collection }) => {
    const isToggling = togglingId === collection.collectionId;

    return (
      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          overflow: "hidden",
          height: "100%",
          transition: "transform .2s ease, box-shadow .2s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: isDark
              ? "0 14px 40px rgba(0,0,0,.22)"
              : "0 14px 40px rgba(50,40,30,.09)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: {
              xs: 180,
              sm: 190,
              md: 200,
            },
            backgroundColor: colors.cardAlt,
            overflow: "hidden",
          }}
        >
          {collection.collectionBanner ? (
            <Box
              component="img"
              src={collection.collectionBanner}
              alt={collection.collectionTitle || "Collection"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                transition: "transform .35s ease",
                "&:hover": {
                  transform: "scale(1.025)",
                },
              }}
            />
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: colors.secondary,
              }}
            >
              <ImageOutlinedIcon sx={{ fontSize: 42 }} />
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top,rgba(0,0,0,.62),transparent 58%)",
            }}
          />

          <Chip
            icon={
              collection.isActive ? (
                <VisibilityOutlinedIcon
                  sx={{
                    fontSize: "14px !important",
                  }}
                />
              ) : (
                <VisibilityOffOutlinedIcon
                  sx={{
                    fontSize: "14px !important",
                  }}
                />
              )
            }
            label={collection.isActive ? "Active" : "Inactive"}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              height: 27,
              borderRadius: "7px",
              fontSize: 10,
              fontWeight: 700,
              color: "#FFFFFF",
              backgroundColor: collection.isActive
                ? "rgba(61,128,84,.88)"
                : "rgba(60,56,52,.88)",
              backdropFilter: "blur(8px)",
              "& .MuiChip-icon": {
                color: "#FFFFFF",
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              display: "flex",
              gap: 0.7,
            }}
          >
            <Tooltip title="Edit collection">
              <IconButton
                size="small"
                onClick={() => openEditDialog(collection)}
                sx={{
                  width: 32,
                  height: 32,
                  color: "#FFFFFF",
                  backgroundColor: "rgba(0,0,0,.48)",
                  backdropFilter: "blur(8px)",
                  "&:hover": {
                    backgroundColor: "rgba(128,107,82,.9)",
                  },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete collection">
              <IconButton
                size="small"
                onClick={() => openDeleteDialog(collection)}
                sx={{
                  width: 32,
                  height: 32,
                  color: "#FFFFFF",
                  backgroundColor: "rgba(0,0,0,.48)",
                  backdropFilter: "blur(8px)",
                  "&:hover": {
                    backgroundColor: "rgba(181,69,69,.92)",
                  },
                }}
              >
                <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              left: 14,
              right: 14,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 650,
                color: "#FFFFFF",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {collection.collectionTitle || "Untitled Collection"}
            </Typography>

            <Typography
              sx={{
                fontSize: 9.5,
                color: "rgba(255,255,255,.72)",
                mt: 0.25,
              }}
            >
              {collection.collectionId}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.2,
            }}
          >
            <Chip
              icon={
                <CollectionsOutlinedIcon
                  sx={{
                    fontSize: "14px !important",
                  }}
                />
              }
              label="Collection"
              size="small"
              sx={{
                height: 24,
                borderRadius: "6px",
                fontSize: 10,
                fontWeight: 650,
                color: colors.accent,
                backgroundColor: colors.accentSoft,
                border: `1px solid ${colors.border}`,
                "& .MuiChip-icon": {
                  color: colors.accent,
                },
              }}
            />

            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
              }}
            >
              {collection.productCount || 0} Products
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  color: colors.secondary,
                }}
              >
                PRODUCTS
              </Typography>

              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 650,
                  color: colors.text,
                  mt: 0.25,
                }}
              >
                {collection.productCount || 0}
              </Typography>
            </Box>

            <Tooltip title="View mobile banner">
              <IconButton
                component="a"
                href={collection.collectionBanner_mob}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "8px",
                  border: `1px solid ${colors.border}`,
                  color: colors.secondary,
                  "&:hover": {
                    color: colors.accent,
                    borderColor: colors.accent,
                    backgroundColor: colors.accentSoft,
                  },
                }}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider
            sx={{
              my: 1.2,
              borderColor: colors.border,
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  color: colors.secondary,
                }}
              >
                STATUS
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 650,
                  color: collection.isActive
                    ? colors.success
                    : colors.secondary,
                  mt: 0.25,
                }}
              >
                {collection.isActive ? "Live collection" : "Currently hidden"}
              </Typography>
            </Box>

            <Tooltip
              title={
                collection.isActive
                  ? "Deactivate collection"
                  : "Activate collection"
              }
            >
              <span>
                <Switch
                  checked={Boolean(collection.isActive)}
                  onChange={() =>
                    handleToggleActive(
                      collection.collectionId,
                      collection.isActive,
                    )
                  }
                  disabled={isToggling}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: colors.success,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: colors.success,
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: isDark ? "#4B4742" : "#C9C2B9",
                    },
                  }}
                />
              </span>
            </Tooltip>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => navigate(`/collection/${collection.collectionId}`)}
            sx={{
              mt: 1.8,
              height: 40,
              borderRadius: "9px",
              borderColor: colors.border,
              color: colors.accent,
              textTransform: "none",
              fontSize: 11.5,
              fontWeight: 650,
              boxShadow: "none",
              "&:hover": {
                borderColor: colors.accent,
                backgroundColor: colors.accentSoft,
                boxShadow: "none",
              },
            }}
          >
            View Products
          </Button>
        </Box>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          backgroundColor: colors.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={28} sx={{ color: colors.accent }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        backgroundColor: colors.background,
        color: colors.text,
        p: {
          xs: 1.5,
          sm: 2.5,
          md: 4,
        },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
        }}
      >
        {/* <Button
          onClick={() => navigate(-1)}
          startIcon={
            <ArrowBackRoundedIcon />
          }
          sx={{
            mb: 2,
            px: 1.5,
            height: 36,
            borderRadius: "8px",
            color: colors.secondary,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.card,
            textTransform: "none",
            fontSize: 11.5,
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              color: colors.accent,
              borderColor: colors.accent,
              backgroundColor:
                colors.accentSoft,
              boxShadow: "none",
            },
          }}
        >
          Back to Home Management
        </Button> */}

        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
              }}
            >
              <CollectionsOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: {
                    xs: 23,
                    sm: 28,
                    md: 30,
                  },
                  fontWeight: 600,
                  lineHeight: 1.1,
                  color: colors.text,
                }}
              >
                Collection Management
              </Typography>

              <Typography
                sx={{
                  fontSize: {
                    xs: 11.5,
                    sm: 13,
                  },
                  color: colors.secondary,
                  mt: 0.5,
                }}
              >
                Manage collections, banners and visibility
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => setOpenDialog(true)}
            fullWidth={isMobile}
            sx={{
              height: 42,
              px: 2,
              borderRadius: "9px",
              backgroundColor: colors.text,
              color: isDark ? "#151412" : "#FFFFFF",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 650,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: colors.accent,
                boxShadow: "none",
              },
            }}
          >
            Add Collection
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(3,1fr)",
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
              }}
            >
              TOTAL COLLECTIONS
            </Typography>

            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 650,
                color: colors.text,
                mt: 0.4,
              }}
            >
              {collections.length}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
              }}
            >
              ACTIVE
            </Typography>

            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 650,
                color: colors.success,
                mt: 0.4,
              }}
            >
              {collections.filter((item) => Boolean(item.isActive)).length}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
              display: {
                xs: "none",
                sm: "block",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
              }}
            >
              TOTAL PRODUCTS
            </Typography>

            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 650,
                color: colors.text,
                mt: 0.4,
              }}
            >
              {collections.reduce(
                (total, collection) =>
                  total + Number(collection.productCount || 0),
                0,
              )}
            </Typography>
          </Paper>
        </Box>

        {refreshing && (
          <LinearProgress
            sx={{
              mb: 2,
              height: 2,
              backgroundColor: colors.accentSoft,
              "& .MuiLinearProgress-bar": {
                backgroundColor: colors.accent,
              },
            }}
          />
        )}

        {collections.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              py: 9,
              px: 3,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: "15px",
                mx: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
                mb: 2,
              }}
            >
              <CollectionsOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>

            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 20,
                fontWeight: 600,
                color: colors.text,
              }}
            >
              No collections yet
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                color: colors.secondary,
                mt: 0.6,
                mb: 2.5,
              }}
            >
              Create your first collection to get started.
            </Typography>

            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                borderRadius: "9px",
                borderColor: colors.border,
                color: colors.accent,
                textTransform: "none",
                fontSize: 12,
                "&:hover": {
                  borderColor: colors.accent,
                  backgroundColor: colors.accentSoft,
                },
              }}
            >
              Create Collection
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {collections.map((collection) => (
              <Grid item xs={12} sm={6} lg={4} key={collection.collectionId}>
                <CollectionCard collection={collection} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* CREATE DIALOG */}

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
          transitionDuration={0}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : "16px",
              backgroundColor: colors.card,
              backgroundImage: "none",
              border: `1px solid ${colors.border}`,
              boxShadow: "0 25px 70px rgba(0,0,0,.2)",
            },
          }}
        >
          {creating && (
            <LinearProgress
              sx={{
                height: 2,
                backgroundColor: colors.accentSoft,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: colors.accent,
                },
              }}
            />
          )}

          <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: 22,
                    fontWeight: 600,
                    color: colors.text,
                  }}
                >
                  Add New Collection
                </Typography>

                <Typography
                  sx={{
                    fontSize: 11,
                    color: colors.secondary,
                    mt: 0.4,
                  }}
                >
                  Create a collection with desktop and mobile banners
                </Typography>
              </Box>

              <IconButton
                onClick={handleCloseDialog}
                disabled={creating}
                sx={{
                  color: colors.secondary,
                  "&:hover": {
                    color: colors.accent,
                    backgroundColor: colors.accentSoft,
                  },
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              <TextField
                label="Collection Title"
                name="collectionTitle"
                fullWidth
                required
                value={newCollection.collectionTitle}
                onChange={handleInputChange}
                disabled={creating}
                placeholder="e.g. Wedding Collection"
                sx={fieldSx}
              />

              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 650,
                    color: colors.text,
                    mb: 0.8,
                  }}
                >
                  Desktop Banner
                </Typography>

                <input
                  id="collection-desktop-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  name="collectionBanner"
                  onChange={handleInputChange}
                />

                {desktopPreview ? (
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={desktopPreview}
                      alt="Desktop preview"
                      sx={{
                        width: "100%",
                        height: 150,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={() => {
                        URL.revokeObjectURL(desktopPreview);

                        setDesktopPreview("");

                        setNewCollection((prev) => ({
                          ...prev,
                          collectionBanner: null,
                        }));
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 32,
                        height: 32,
                        bgcolor: "rgba(0,0,0,.6)",
                        color: "#fff",
                        "&:hover": {
                          bgcolor: "rgba(181,69,69,.9)",
                        },
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    component="label"
                    htmlFor="collection-desktop-upload"
                    sx={{
                      display: "block",
                      border: `1px dashed ${colors.border}`,
                      borderRadius: "11px",
                      p: 2,
                      cursor: "pointer",
                      backgroundColor: colors.cardAlt,
                      "&:hover": {
                        borderColor: colors.accent,
                        backgroundColor: colors.accentSoft,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "9px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: colors.accentSoft,
                          color: colors.accent,
                        }}
                      >
                        <CloudUploadOutlinedIcon />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 650,
                            color: colors.text,
                          }}
                        >
                          Upload desktop banner
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 10.5,
                            color: colors.secondary,
                            mt: 0.25,
                          }}
                        >
                          PNG, JPG or WEBP • Max 5 MB
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {newCollection.collectionBanner && (
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: colors.secondary,
                      mt: 0.7,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {newCollection.collectionBanner.name}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 650,
                    color: colors.text,
                    mb: 0.8,
                  }}
                >
                  Mobile Banner
                </Typography>

                <input
                  id="collection-mobile-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  name="collectionBanner_mob"
                  onChange={handleInputChange}
                />

                {mobilePreview ? (
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={mobilePreview}
                      alt="Mobile preview"
                      sx={{
                        width: "100%",
                        height: 150,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={() => {
                        URL.revokeObjectURL(mobilePreview);

                        setMobilePreview("");

                        setNewCollection((prev) => ({
                          ...prev,
                          collectionBanner_mob: null,
                        }));
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 32,
                        height: 32,
                        bgcolor: "rgba(0,0,0,.6)",
                        color: "#fff",
                        "&:hover": {
                          bgcolor: "rgba(181,69,69,.9)",
                        },
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    component="label"
                    htmlFor="collection-mobile-upload"
                    sx={{
                      display: "block",
                      border: `1px dashed ${colors.border}`,
                      borderRadius: "11px",
                      p: 2,
                      cursor: "pointer",
                      backgroundColor: colors.cardAlt,
                      "&:hover": {
                        borderColor: colors.accent,
                        backgroundColor: colors.accentSoft,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "9px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: colors.accentSoft,
                          color: colors.accent,
                        }}
                      >
                        <CloudUploadOutlinedIcon />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 650,
                            color: colors.text,
                          }}
                        >
                          Upload mobile banner
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 10.5,
                            color: colors.secondary,
                            mt: 0.25,
                          }}
                        >
                          PNG, JPG or WEBP • Max 5 MB
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {newCollection.collectionBanner_mob && (
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: colors.secondary,
                      mt: 0.7,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {newCollection.collectionBanner_mob.name}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "9px",
                  backgroundColor: colors.accentSoft,
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <CheckCircleOutlineRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: colors.accent,
                    mt: 0.1,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: colors.secondary,
                    lineHeight: 1.5,
                  }}
                >
                  Use high-quality landscape artwork for desktop and a
                  portrait-friendly design for mobile.
                </Typography>
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              pt: 1,
              gap: 1,
            }}
          >
            <Button
              onClick={handleCloseDialog}
              disabled={creating}
              sx={{
                height: 40,
                px: 2,
                borderRadius: "9px",
                color: colors.secondary,
                textTransform: "none",
                fontSize: 12,
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleAddCollection}
              variant="contained"
              disabled={creating}
              startIcon={
                creating ? (
                  <CircularProgress
                    size={16}
                    sx={{
                      color: "inherit",
                    }}
                  />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              sx={{
                height: 40,
                px: 2.5,
                borderRadius: "9px",
                backgroundColor: colors.text,
                color: isDark ? "#151412" : "#FFFFFF",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 650,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: colors.accent,
                  boxShadow: "none",
                },
              }}
            >
              {creating ? "Creating..." : "Create Collection"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* EDIT DIALOG */}

        <Dialog
          open={editDialog}
          onClose={closeEditDialog}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
          transitionDuration={0}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : "16px",
              backgroundColor: colors.card,
              backgroundImage: "none",
              border: `1px solid ${colors.border}`,
              boxShadow: "0 25px 70px rgba(0,0,0,.2)",
            },
          }}
        >
          {editing && (
            <LinearProgress
              sx={{
                height: 2,
                backgroundColor: colors.accentSoft,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: colors.accent,
                },
              }}
            />
          )}

          <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: 22,
                    fontWeight: 600,
                    color: colors.text,
                  }}
                >
                  Edit Collection
                </Typography>

                <Typography
                  sx={{
                    fontSize: 11,
                    color: colors.secondary,
                    mt: 0.4,
                  }}
                >
                  Update collection details and banners
                </Typography>
              </Box>

              <IconButton
                onClick={closeEditDialog}
                disabled={editing}
                sx={{
                  color: colors.secondary,
                  "&:hover": {
                    color: colors.accent,
                    backgroundColor: colors.accentSoft,
                  },
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              <TextField
                label="Collection Title"
                fullWidth
                required
                value={editCollectionData.collectionTitle}
                onChange={(e) =>
                  setEditCollectionData((prev) => ({
                    ...prev,
                    collectionTitle: e.target.value,
                  }))
                }
                disabled={editing}
                sx={fieldSx}
              />

              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 650,
                    color: colors.text,
                    mb: 0.8,
                  }}
                >
                  Desktop Banner
                </Typography>

                <input
                  id="edit-collection-desktop-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  name="collectionBanner"
                  onChange={handleEditInputChange}
                />

                {editDesktopPreview ? (
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={editDesktopPreview}
                      alt="Desktop banner"
                      sx={{
                        width: "100%",
                        height: 150,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        right: 8,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        component="label"
                        htmlFor="edit-collection-desktop-upload"
                        size="small"
                        startIcon={<CloudUploadOutlinedIcon />}
                        disabled={editing}
                        sx={{
                          borderRadius: "8px",
                          backgroundColor: "rgba(0,0,0,.68)",
                          color: "#fff",
                          textTransform: "none",
                          fontSize: 10,
                          "&:hover": {
                            backgroundColor: "rgba(128,107,82,.92)",
                          },
                        }}
                      >
                        Replace
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    component="label"
                    htmlFor="edit-collection-desktop-upload"
                    sx={{
                      display: "block",
                      border: `1px dashed ${colors.border}`,
                      borderRadius: "11px",
                      p: 2,
                      cursor: "pointer",
                      backgroundColor: colors.cardAlt,
                      "&:hover": {
                        borderColor: colors.accent,
                        backgroundColor: colors.accentSoft,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <CloudUploadOutlinedIcon
                        sx={{
                          color: colors.accent,
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: 12,
                          color: colors.text,
                        }}
                      >
                        Upload desktop banner
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 650,
                    color: colors.text,
                    mb: 0.8,
                  }}
                >
                  Mobile Banner
                </Typography>

                <input
                  id="edit-collection-mobile-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  name="collectionBanner_mob"
                  onChange={handleEditInputChange}
                />

                {editMobilePreview ? (
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={editMobilePreview}
                      alt="Mobile banner"
                      sx={{
                        width: "100%",
                        height: 150,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        right: 8,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        component="label"
                        htmlFor="edit-collection-mobile-upload"
                        size="small"
                        startIcon={<CloudUploadOutlinedIcon />}
                        disabled={editing}
                        sx={{
                          borderRadius: "8px",
                          backgroundColor: "rgba(0,0,0,.68)",
                          color: "#fff",
                          textTransform: "none",
                          fontSize: 10,
                          "&:hover": {
                            backgroundColor: "rgba(128,107,82,.92)",
                          },
                        }}
                      >
                        Replace
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    component="label"
                    htmlFor="edit-collection-mobile-upload"
                    sx={{
                      display: "block",
                      border: `1px dashed ${colors.border}`,
                      borderRadius: "11px",
                      p: 2,
                      cursor: "pointer",
                      backgroundColor: colors.cardAlt,
                      "&:hover": {
                        borderColor: colors.accent,
                        backgroundColor: colors.accentSoft,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <CloudUploadOutlinedIcon
                        sx={{
                          color: colors.accent,
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: 12,
                          color: colors.text,
                        }}
                      >
                        Upload mobile banner
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "9px",
                  backgroundColor: colors.accentSoft,
                  display: "flex",
                  gap: 1,
                }}
              >
                <CheckCircleOutlineRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: colors.accent,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: colors.secondary,
                    lineHeight: 1.5,
                  }}
                >
                  Existing banners remain unchanged unless you upload a
                  replacement.
                </Typography>
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              pt: 1,
              gap: 1,
            }}
          >
            <Button
              onClick={closeEditDialog}
              disabled={editing}
              sx={{
                height: 40,
                px: 2,
                borderRadius: "9px",
                color: colors.secondary,
                textTransform: "none",
                fontSize: 12,
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleEditCollection}
              variant="contained"
              disabled={editing}
              startIcon={
                editing ? (
                  <CircularProgress
                    size={16}
                    sx={{
                      color: "inherit",
                    }}
                  />
                ) : (
                  <CheckCircleOutlineRoundedIcon />
                )
              }
              sx={{
                height: 40,
                px: 2.5,
                borderRadius: "9px",
                backgroundColor: colors.text,
                color: isDark ? "#151412" : "#FFFFFF",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 650,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: colors.accent,
                  boxShadow: "none",
                },
              }}
            >
              {editing ? "Updating..." : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* STATUS CONFIRMATION */}

        <Dialog
          open={statusDialog}
          onClose={closeStatusDialog}
          fullWidth
          maxWidth="xs"
          transitionDuration={0}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              backgroundColor: colors.card,
              backgroundImage: "none",
              border: `1px solid ${colors.border}`,
              boxShadow: "0 25px 70px rgba(0,0,0,.22)",
            },
          }}
        >
          <DialogTitle sx={{ p: 2.5, pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "11px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: colors.accentSoft,
                  color: colors.accent,
                }}
              >
                {statusCollection?.isActive ? (
                  <VisibilityOffOutlinedIcon />
                ) : (
                  <VisibilityOutlinedIcon />
                )}
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: 21,
                    fontWeight: 600,
                    color: colors.text,
                  }}
                >
                  {statusCollection?.isActive
                    ? "Deactivate Collection?"
                    : "Activate Collection?"}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 11,
                    color: colors.secondary,
                    mt: 0.3,
                  }}
                >
                  {statusCollection?.collectionTitle}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: 2.5, py: 1.5 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: colors.secondary,
                lineHeight: 1.6,
              }}
            >
              {statusCollection?.isActive
                ? "This collection will be hidden from active collection displays. You can activate it again anytime."
                : "This collection will become visible as an active collection."}
            </Typography>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              pt: 1,
              gap: 1,
            }}
          >
            <Button
              onClick={closeStatusDialog}
              disabled={statusChanging}
              sx={{
                height: 40,
                px: 2,
                borderRadius: "9px",
                color: colors.secondary,
                textTransform: "none",
                fontSize: 12,
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={confirmToggleActive}
              variant="contained"
              disabled={statusChanging}
              startIcon={
                statusChanging ? (
                  <CircularProgress
                    size={15}
                    sx={{
                      color: "inherit",
                    }}
                  />
                ) : statusCollection?.isActive ? (
                  <VisibilityOffOutlinedIcon />
                ) : (
                  <VisibilityOutlinedIcon />
                )
              }
              sx={{
                height: 40,
                px: 2,
                borderRadius: "9px",
                backgroundColor: statusCollection?.isActive
                  ? colors.danger
                  : colors.success,
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 650,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: statusCollection?.isActive
                    ? "#9F3E3E"
                    : "#326A45",
                  boxShadow: "none",
                },
              }}
            >
              {statusChanging
                ? "Updating..."
                : statusCollection?.isActive
                  ? "Deactivate"
                  : "Activate"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DELETE CONFIRMATION */}

        <Dialog
          open={deleteDialog}
          onClose={closeDeleteDialog}
          fullWidth
          maxWidth="xs"
          transitionDuration={0}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              backgroundColor: colors.card,
              backgroundImage: "none",
              border: `1px solid ${colors.border}`,
              boxShadow: "0 25px 70px rgba(0,0,0,.22)",
            },
          }}
        >
          <DialogTitle sx={{ p: 2.5, pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "11px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isDark
                    ? "rgba(217,148,148,.12)"
                    : "rgba(181,69,69,.08)",
                  color: colors.danger,
                }}
              >
                <WarningAmberRoundedIcon />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: 21,
                    fontWeight: 600,
                    color: colors.text,
                  }}
                >
                  Delete Collection?
                </Typography>

                <Typography
                  sx={{
                    fontSize: 11,
                    color: colors.secondary,
                    mt: 0.3,
                  }}
                >
                  {selectedCollection?.collectionTitle}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: 2.5, py: 1.5 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: isDark
                  ? "rgba(217,148,148,.07)"
                  : "rgba(181,69,69,.05)",
                border: `1px solid ${
                  isDark ? "rgba(217,148,148,.15)" : "rgba(181,69,69,.12)"
                }`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: colors.secondary,
                  lineHeight: 1.6,
                }}
              >
                This will permanently delete the collection and its collection
                banners. Products themselves will not be deleted.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              pt: 1,
              gap: 1,
            }}
          >
            <Button
              onClick={closeDeleteDialog}
              disabled={deleting}
              sx={{
                height: 40,
                px: 2,
                borderRadius: "9px",
                color: colors.secondary,
                textTransform: "none",
                fontSize: 12,
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleDeleteCollection}
              variant="contained"
              disabled={deleting}
              startIcon={
                deleting ? (
                  <CircularProgress
                    size={15}
                    sx={{
                      color: "inherit",
                    }}
                  />
                ) : (
                  <DeleteOutlineRoundedIcon />
                )
              }
              sx={{
                height: 40,
                px: 2,
                borderRadius: "9px",
                backgroundColor: colors.danger,
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 650,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#9F3E3E",
                  boxShadow: "none",
                },
              }}
            >
              {deleting ? "Deleting..." : "Delete Collection"}
            </Button>
          </DialogActions>
        </Dialog>

        {error && (
          <Box
            sx={{
              position: "fixed",
              right: {
                xs: 12,
                sm: 24,
              },
              bottom: {
                xs: 12,
                sm: 24,
              },
              zIndex: 9999,
              maxWidth: {
                xs: "calc(100vw - 24px)",
                sm: 420,
              },
            }}
          >
            <Alert
              severity="error"
              variant="filled"
              onClose={() => setError("")}
              sx={{
                borderRadius: "10px",
                boxShadow: "0 10px 30px rgba(0,0,0,.18)",
              }}
            >
              {error}
            </Alert>
          </Box>
        )}

        {success && (
          <Box
            sx={{
              position: "fixed",
              right: {
                xs: 12,
                sm: 24,
              },
              bottom: {
                xs: 12,
                sm: 24,
              },
              zIndex: 9999,
              maxWidth: {
                xs: "calc(100vw - 24px)",
                sm: 420,
              },
            }}
          >
            <Alert
              severity="success"
              variant="filled"
              onClose={() => setSuccess("")}
              sx={{
                borderRadius: "10px",
                boxShadow: "0 10px 30px rgba(0,0,0,.18)",
              }}
            >
              {success}
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CollectionManagement;
